<template>
  <div class="smart-values-panel">
    <!-- Search -->
    <div class="sv-search">
      <div class="sv-search-wrap">
        <i class="tim-icons icon-zoom-split sv-search-icon"></i>
        <input
          ref="searchInput"
          type="text"
          v-model="searchQuery"
          placeholder="Search variables..."
          class="sv-search-input"
        >
        <button v-if="searchQuery" class="sv-search-clear" @click="searchQuery = ''">
          <i class="tim-icons icon-simple-remove"></i>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="sv-loading">
      <i class="tim-icons icon-refresh-02 spin"></i>
    </div>

    <!-- Search results (flat list) -->
    <div v-else-if="searchQuery" class="sv-list">
      <div
        v-for="value in filteredValues"
        :key="value.id"
        :class="['sv-var', { inserted: insertedValueId === value.id, copied: copiedValueId === value.id }]"
        @click="insertValue(value)"
      >
        <div class="sv-var-info">
          <span class="sv-var-name">{{ value.display_name }}</span>
          <span class="sv-var-key">{{ value.syntax }}</span>
          <span v-if="value.description" class="sv-var-desc">{{ value.description }}</span>
        </div>
        <button class="sv-copy" @click.stop="copyValue(value)">
          <i :class="copiedValueId === value.id ? 'tim-icons icon-check-2' : 'tim-icons icon-single-copy-04'"></i>
          {{ copiedValueId === value.id ? 'Copied' : 'Copy' }}
        </button>
      </div>
      <div v-if="filteredValues.length === 0" class="sv-empty">
        No results for "{{ searchQuery }}"
      </div>
    </div>

    <!-- Category detail view -->
    <div v-else-if="selectedCategory" class="sv-list">
      <button class="sv-back" @click="selectedCategory = null">
        <i class="tim-icons icon-minimal-left"></i>
        {{ formatCategoryName(selectedCategory) }}
      </button>
      <div
        v-for="value in categoryValues"
        :key="value.id"
        :class="['sv-var', { inserted: insertedValueId === value.id, copied: copiedValueId === value.id }]"
        @click="insertValue(value)"
      >
        <div class="sv-var-info">
          <span class="sv-var-name">{{ value.display_name }}</span>
          <span class="sv-var-key">{{ value.syntax }}</span>
          <span v-if="value.description" class="sv-var-desc">{{ value.description }}</span>
        </div>
        <button class="sv-copy" @click.stop="copyValue(value)">
          <i :class="copiedValueId === value.id ? 'tim-icons icon-check-2' : 'tim-icons icon-single-copy-04'"></i>
          {{ copiedValueId === value.id ? 'Copied' : 'Copy' }}
        </button>
      </div>
    </div>

    <!-- Category list view -->
    <div v-else class="sv-list">
      <div
        v-for="cat in visibleCategories"
        :key="cat.name"
        class="sv-cat"
        @click="selectedCategory = cat.name"
      >
        <div class="sv-cat-icon-wrap">
          <i :class="getCategoryIcon(cat.name)"></i>
        </div>
        <div class="sv-cat-info">
          <span class="sv-cat-name">{{ formatCategoryName(cat.name) }}</span>
          <span class="sv-cat-desc">{{ getCategoryDesc(cat.name) }}</span>
        </div>
        <i class="tim-icons icon-minimal-right sv-cat-arrow"></i>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { API_LOCATION } from '@/api-config'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { diacriticIncludes } from '@/util/search'

interface SmartValue {
  id: number
  variable_key: string
  display_name: string
  description: string
  example_value: string
  category: string
  source_table: string
  source_field: string
  syntax: string
}

interface CategoryGroup {
  name: string
  count: number
  values: SmartValue[]
}

const emit = defineEmits<{
  (e: 'insert-value', value: SmartValue): void
  (e: 'close'): void
}>()

const searchInput = ref<HTMLInputElement | null>(null)
const loading = ref(false)
const searchQuery = ref('')
const selectedCategory = ref<string | null>(null)
const allValues = ref<SmartValue[]>([])
const copiedValueId = ref<number | null>(null)
const insertedValueId = ref<number | null>(null)

const props = defineProps<{
  /**
   * Restrict the panel to these variable keys. Omit to show the whole catalog
   * (the email builder does, and must keep doing so).
   *
   * Exists because a channel can only offer variables its renderer resolves.
   * An unresolved token ships to the customer as literal "{{booking.foo}}" —
   * tolerable to spot in an email draft, but in SMS it is plainly visible,
   * costs a segment, and cannot be recalled.
   */
  allowedKeys?: string[]
}>()

// Normalized so callers can pass either "customer.firstName" or "{{customer.firstName}}"
// without having to know which shape the catalog stores.
function normalizeKey(value: string): string {
  return (value || '').replace(/[{}]/g, '').trim()
}

const availableValues = computed((): SmartValue[] => {
  if (!props.allowedKeys || props.allowedKeys.length === 0) return allValues.value
  const allowed = new Set(props.allowedKeys.map(normalizeKey))
  return allValues.value.filter(v =>
    allowed.has(normalizeKey(v.variable_key)) || allowed.has(normalizeKey(v.syntax))
  )
})

const categoryMeta: Record<string, { icon: string, desc: string }> = {
  customer: { icon: 'tim-icons icon-single-02', desc: 'Customer name, email, phone, and address fields' },
  booking: { icon: 'tim-icons icon-calendar-60', desc: 'Booking dates, times, status, and pricing' },
  company: { icon: 'tim-icons icon-bank', desc: 'Your business name, contact info, and address' },
  'company group': { icon: 'tim-icons icon-world', desc: 'Location-specific name, contact, and address' },
  game: { icon: 'tim-icons icon-puzzle-10', desc: 'Game details, duration, difficulty, and game master info' },
  'Gift Card': { icon: 'tim-icons icon-tag', desc: 'Gift card code, value, recipient, and expiration' },
  payment: { icon: 'tim-icons icon-credit-card', desc: 'Payment method, card details, and amounts' },
  staff: { icon: 'tim-icons icon-badge', desc: 'Staff member name, email, and contact info' },
  system: { icon: 'tim-icons icon-settings-gear-63', desc: 'Current date, time, and system values' },
  transaction: { icon: 'tim-icons icon-wallet-43', desc: 'Order totals, fees, taxes, and billing info' },
  'activity log': { icon: 'tim-icons icon-paper', desc: 'Event descriptions, timestamps, and change details' },
  automation: { icon: 'tim-icons icon-refresh-02', desc: 'Automation rule name, trigger, and execution time' },
  'project management': { icon: 'tim-icons icon-components', desc: 'Ticket number, title, assignee, and project info' },
  'promo code': { icon: 'tim-icons icon-tag', desc: 'Promo code issued by a campaign and its validity dates' }
}

const filteredValues = computed(() => {
  if (!searchQuery.value) return availableValues.value
  const q = searchQuery.value.toLowerCase()
  return availableValues.value.filter(v =>
    diacriticIncludes(v.display_name, q) ||
    diacriticIncludes(v.syntax, q) ||
    (v.description && diacriticIncludes(v.description, q))
  )
})

const visibleCategories = computed((): CategoryGroup[] => {
  const map = new Map<string, SmartValue[]>()
  for (const v of availableValues.value) {
    const cat = v.category || 'other'
    if (!map.has(cat)) map.set(cat, [])
    map.get(cat)!.push(v)
  }
  return Array.from(map.entries())
    .map(([name, values]) => ({ name, count: values.length, values }))
    .sort((a, b) => a.name.localeCompare(b.name))
})

const categoryValues = computed(() => {
  if (!selectedCategory.value) return []
  return availableValues.value.filter(v => v.category === selectedCategory.value)
})

async function getAuthToken(): Promise<string> {
  const data = await Auth.currentSession()
  return data.getIdToken().getJwtToken()
}

async function loadSmartValues() {
  loading.value = true
  try {
    const token = await getAuthToken()
    const response = await axios.get(`${API_LOCATION}smart-values`, {
      headers: { Authorization: token }
    })
    if (response.data) {
      allValues.value = (response.data.values || []) as SmartValue[]
    }
  } catch (error) {
    console.error('Failed to load smart values:', error)
  } finally {
    loading.value = false
  }
}

function formatCategoryName(category: string): string {
  return category.charAt(0).toUpperCase() + category.slice(1)
}

function getCategoryIcon(category: string): string {
  return categoryMeta[category]?.icon || 'tim-icons icon-bullet-list-67'
}

function getCategoryDesc(category: string): string {
  return categoryMeta[category]?.desc || ''
}

function insertValue(value: SmartValue) {
  emit('insert-value', value)
  insertedValueId.value = value.id
  setTimeout(() => { insertedValueId.value = null }, 500)
}

async function copyValue(value: SmartValue) {
  try {
    await navigator.clipboard.writeText(value.syntax)
  } catch {
    const ta = document.createElement('textarea')
    ta.value = value.syntax
    ta.style.cssText = 'position:fixed;opacity:0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
  }
  copiedValueId.value = value.id
  setTimeout(() => { copiedValueId.value = null }, 1500)
}

onMounted(async () => {
  await loadSmartValues()
  await nextTick()
  searchInput.value?.focus()
})
</script>

<script lang="ts">
export default {
  name: 'SmartValuesPanel'
}
</script>

<style scoped>
.smart-values-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #171826;
}

/* ---- Search ---- */
.sv-search {
  padding: 0.625rem 0.75rem;
  flex-shrink: 0;
}
.sv-search-wrap {
  position: relative;
}
.sv-search-icon {
  position: absolute;
  left: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255,255,255,0.3);
  font-size: 0.7rem;
  pointer-events: none;
}
.sv-search-input {
  width: 100%;
  padding: 0.4rem 1.5rem 0.4rem 1.75rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 4px;
  color: #fff;
  font-size: 0.75rem;
}
.sv-search-input:focus { outline: none; border-color: #e14eca; }
.sv-search-input::placeholder { color: rgba(255,255,255,0.3); }
.sv-search-clear {
  position: absolute;
  right: 0.375rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: rgba(255,255,255,0.4);
  cursor: pointer;
  font-size: 0.55rem;
  padding: 2px;
}

/* ---- Scrollable list area ---- */
.sv-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 0.75rem 0.75rem;
}
.sv-list::-webkit-scrollbar { width: 4px; }
.sv-list::-webkit-scrollbar-track { background: transparent; }
.sv-list::-webkit-scrollbar-thumb { background: #393c54; border-radius: 2px; }
.sv-list::-webkit-scrollbar-thumb:hover { background: #555770; }
.sv-list { scrollbar-width: thin; scrollbar-color: #393c54 transparent; }

/* ---- Category cards ---- */
.sv-cat {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem;
  margin-bottom: 0.375rem;
  background: #22233a;
  border: 1px solid #393c54;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
}
.sv-cat:hover {
  border-color: rgba(225,78,202,0.4);
  background: rgba(39,41,61,0.8);
}
.sv-cat-icon-wrap {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(225,78,202,0.1);
  border-radius: 6px;
  flex-shrink: 0;
  color: #e14eca;
  font-size: 0.85rem;
}
.sv-cat-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sv-cat-name {
  color: rgba(255,255,255,0.9);
  font-size: 0.8rem;
  font-weight: 500;
}
.sv-cat-desc {
  color: rgba(255,255,255,0.4);
  font-size: 0.675rem;
  word-break: normal;
  overflow-wrap: normal;
}
.sv-cat-arrow {
  color: rgba(255,255,255,0.25);
  font-size: 0.7rem;
  flex-shrink: 0;
}

/* ---- Back button ---- */
.sv-back {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: #e14eca;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 0.375rem;
  transition: opacity 0.15s;
  width: 100%;
  text-align: left;
}
.sv-back:hover { opacity: 0.8; }
.sv-back i { font-size: 0.65rem; }

/* ---- Variable rows ---- */
.sv-var {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.5rem 0.375rem;
  cursor: pointer;
  transition: background 0.12s;
  border-bottom: 1px solid rgba(57,60,84,0.2);
  border-radius: 4px;
}
.sv-var:hover {
  background: rgba(225,78,202,0.06);
}
.sv-var:active {
  background: rgba(39,174,96,0.15);
}
.sv-var.inserted { background: rgba(39,174,96,0.12); }
.sv-var.copied .sv-copy { color: #27AE60; border-color: rgba(39,174,96,0.4); background: rgba(39,174,96,0.08); }

.sv-var-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.sv-var-name {
  color: rgba(255,255,255,0.9);
  font-size: 0.75rem;
  font-weight: 500;
}
.sv-var-key {
  font-family: 'Monaco','Menlo',monospace;
  font-size: 0.6rem;
  color: rgba(225,78,202,0.6);
}
.sv-var-desc {
  color: rgba(255,255,255,0.45);
  font-size: 0.65rem;
  line-height: 1.3;
  margin-top: 1px;
}

/* ---- Copy button ---- */
.sv-copy {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.45);
  cursor: pointer;
  padding: 4px 8px;
  font-size: 0.6rem;
  transition: all 0.15s;
  flex-shrink: 0;
  border-radius: 4px;
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.sv-copy:hover {
  color: #e14eca;
  border-color: rgba(225,78,202,0.4);
  background: rgba(225,78,202,0.08);
}

/* ---- States ---- */
.sv-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #e14eca;
  font-size: 1rem;
}
.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.sv-empty {
  padding: 1.5rem;
  text-align: center;
  color: rgba(255,255,255,0.4);
  font-size: 0.725rem;
}

/* Light mode. The panel is dark hardcoded above; these counterparts keep it
   usable wherever it mounts (builder popover, custom HTML drawer). */
.white-content .smart-values-panel {
  background: #ffffff;
}
.white-content .sv-search-input {
  background: #ffffff;
  color: rgba(0, 0, 0, 0.85);
  border-color: #e3e3e3;
}
.white-content .sv-search-input::placeholder,
.white-content .sv-search-icon {
  color: rgba(0, 0, 0, 0.35);
}
.white-content .sv-search-clear {
  color: rgba(0, 0, 0, 0.4);
}
.white-content .sv-cat,
.white-content .sv-var {
  background: #f7f7f8;
}
.white-content .sv-cat:hover,
.white-content .sv-var:hover {
  background: rgba(225, 78, 202, 0.08);
}
.white-content .sv-cat-name,
.white-content .sv-var-name,
.white-content .sv-back {
  color: rgba(0, 0, 0, 0.85);
}
.white-content .sv-cat-desc,
.white-content .sv-var-desc {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .sv-cat-arrow {
  color: rgba(0, 0, 0, 0.3);
}
.white-content .sv-list::-webkit-scrollbar-thumb {
  background: #d0d0d8;
}

</style>
